import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
    Row, Col, Typography, Form, Input, Button, Checkbox, Select,
    Card, Divider, Space, Tag, Flex, AutoComplete,
} from 'antd';
import { PlusOutlined, DeleteOutlined, PlayCircleOutlined, StopOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { startSession, stopSession, getRunningSession, getActivityOptions, getKundenOptions } from './redux/slice';

const { Title, Text } = Typography;

const pad = n => String(n).padStart(2, '0');
const fmtSeconds = s => `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;

// ── Single running timer card ────────────────────────────────────────────────
const RunningTimerCard = ({ session, activityOptions, onStop, t }) => {
    const [elapsed, setElapsed] = useState(0);
    const [kundenId, setKundenId] = useState(session.kundenId || '');
    const [kundenName, setKundenName] = useState(session.kundenName || '');
    const [ansprechpartner, setAnsprechpartner] = useState(session.ansprechpartner || '');
    const [taetigkeiten, setTaetigkeiten] = useState(
        session.taetigkeiten?.length
            ? session.taetigkeiten.map((t, i) => ({ key: i, ...t }))
            : [{ key: 0, taetigkeitId: null, taetigkeitName: '', freitext: '' }]
    );
    const [berechenbar, setBerechenbar] = useState(session.berechenbar !== undefined ? session.berechenbar : true);
    const timerRef = useRef(null);

    useEffect(() => {
        const start = new Date(session.startTime).getTime();
        const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
        tick();
        timerRef.current = setInterval(tick, 1000);
        return () => clearInterval(timerRef.current);
    }, [session._id]);

    const addRow = () =>
        setTaetigkeiten(prev => [...prev, { key: Date.now(), taetigkeitId: null, taetigkeitName: '', freitext: '' }]);

    const removeRow = key =>
        setTaetigkeiten(prev => prev.filter(r => r.key !== key));

    const updateRow = (key, field, value) =>
        setTaetigkeiten(prev => prev.map(r => {
            if (r.key !== key) return r;
            if (field === 'taetigkeitId') {
                const opt = activityOptions.find(o => String(o.value) === String(value));
                return { ...r, taetigkeitId: value, taetigkeitName: opt?.label || '' };
            }
            return { ...r, [field]: value };
        }));

    const handleStop = () => {
        clearInterval(timerRef.current);
        const built = taetigkeiten
            .filter(t => t.taetigkeitId || t.freitext)
            .map(t => ({
                taetigkeitId: t.taetigkeitId || null,
                taetigkeitName: t.taetigkeitName || '',
                freitext: t.freitext || '',
            }));
        onStop(session._id, built, berechenbar, kundenId, kundenName, ansprechpartner);
    };

    return (
        <Card
            style={{ borderLeft: '4px solid #52c41a' }}
            size='small'
            extra={
                <Tag color='green' style={{ fontSize: 18, padding: '2px 12px', fontFamily: 'monospace' }}>
                    {fmtSeconds(elapsed)}
                </Tag>
            }
            title={<Text type='success'>{t('support_neue_sitzung')}</Text>}
        >
            <Row gutter={8} style={{ marginBottom: 10 }}>
                <Col xs={24} sm={8} md={8}>
                    <Input
                        placeholder={t('support_kundenId')}
                        value={kundenId}
                        onChange={e => setKundenId(e.target.value)}
                    />
                </Col>
                <Col xs={24} sm={8} md={8}>
                    <Input
                        placeholder={t('support_kundenName')}
                        value={kundenName}
                        onChange={e => setKundenName(e.target.value)}
                    />
                </Col>
                <Col xs={24} sm={8} md={8}>
                    <Input
                        placeholder={t('support_ansprechpartner')}
                        value={ansprechpartner}
                        onChange={e => setAnsprechpartner(e.target.value)}
                    />
                </Col>
            </Row>

            {taetigkeiten.map((row, idx) => (
                <Row key={row.key} gutter={8} align='middle' style={{ marginBottom: 6 }}>
                    <Col flex='none'>
                        <Space>
                            <Button icon={<PlusOutlined />} onClick={addRow} />
                            {taetigkeiten.length > 1 && (
                                <Button icon={<DeleteOutlined />} danger onClick={() => removeRow(row.key)} />
                            )}
                        </Space>
                    </Col>
                    <Col flex='1'>
                        <Select
                            allowClear
                            showSearch
                            placeholder={t('support_taetigkeit_waehlen')}
                            style={{ width: '100%' }}
                            options={activityOptions}
                            value={row.taetigkeitId || undefined}
                            onChange={v => updateRow(row.key, 'taetigkeitId', v)}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col flex='1'>
                        <Input
                            placeholder={t('support_freitext')}
                            value={row.freitext}
                            onChange={e => updateRow(row.key, 'freitext', e.target.value)}
                        />
                    </Col>
                </Row>
            ))}

            <Row justify='space-between' align='middle' style={{ marginTop: 8 }}>
                <Checkbox
                    checked={berechenbar}
                    onChange={e => setBerechenbar(e.target.checked)}
                >
                    <Text style={{ fontSize: 13 }}>{t('support_berechenbar')}</Text>
                </Checkbox>
                <Button danger icon={<StopOutlined />} onClick={handleStop}>
                    {t('support_stop')}
                </Button>
            </Row>
        </Card>
    );
};

// ── Main page ────────────────────────────────────────────────────────────────
const SupporterfassungPage = () => {
    const dispatch = useDispatch();
    const { auth, supportTracking } = useSelector(s => s);
    const { t } = useTranslation();
    const [form] = Form.useForm();

    const { runningSessions, loading, activityOptions, kundenOptions } = supportTracking;
    const organizer = auth?.decodeToken?.organizer;
    const userId = auth?.decodeToken?._id;

    const [taetigkeiten, setTaetigkeiten] = useState([
        { key: Date.now(), taetigkeitId: null, taetigkeitName: '', freitext: '' },
    ]);
    const [berechenbar, setBerechenbar] = useState(true);

    useEffect(() => {
        if (organizer) {
            dispatch(getActivityOptions(organizer));
            dispatch(getRunningSession(userId));
            dispatch(getKundenOptions(organizer));
        }
    }, [organizer]);

    const handleStart = async () => {
        try {
            const values = await form.validateFields();
            await dispatch(startSession({
                organizer,
                user: userId,
                kundenId: values.kundenId || '',
                kundenName: values.kundenName || '',
                ansprechpartner: values.ansprechpartner || '',
                taetigkeiten: buildTaetigkeiten(),
                berechenbar,
            }));
            form.resetFields();
            setTaetigkeiten([{ key: Date.now(), taetigkeitId: null, taetigkeitName: '', freitext: '' }]);
            setBerechenbar(true);
        } catch (_) {}
    };

    const handleStop = async (id, taetigkeitenData, berechenbar, kundenId, kundenName, ansprechpartner) => {
        await dispatch(stopSession({ id, taetigkeiten: taetigkeitenData, berechenbar, kundenId, kundenName, ansprechpartner }));
    };

    const buildTaetigkeiten = () =>
        taetigkeiten
            .filter(t => t.taetigkeitId || t.freitext)
            .map(t => ({
                taetigkeitId: t.taetigkeitId || null,
                taetigkeitName: t.taetigkeitName || '',
                freitext: t.freitext || '',
            }));

    const addRow = () =>
        setTaetigkeiten(prev => [...prev, { key: Date.now(), taetigkeitId: null, taetigkeitName: '', freitext: '' }]);

    const removeRow = key =>
        setTaetigkeiten(prev => prev.filter(r => r.key !== key));

    const updateRow = (key, field, value) =>
        setTaetigkeiten(prev => prev.map(r => {
            if (r.key !== key) return r;
            if (field === 'taetigkeitId') {
                const opt = activityOptions.find(o => String(o.value) === String(value));
                return { ...r, taetigkeitId: value, taetigkeitName: opt?.label || '' };
            }
            return { ...r, [field]: value };
        }));

    return (
        <Fragment>
            <Flex vertical gap={16}>
                <Row justify='space-between' align='middle' className='padding-1 rounded-1'>
                    <Col>
                        <Title level={4} type='success'>{t('support_erfassung_title')}</Title>
                    </Col>
                    {runningSessions.length > 0 && (
                        <Col>
                            <Tag color='green'>{runningSessions.length} {t('support_aktive_sitzungen')}</Tag>
                        </Col>
                    )}
                </Row>

                {/* Active timers */}
                {runningSessions.map(session => (
                    <RunningTimerCard
                        key={session._id}
                        session={session}
                        activityOptions={activityOptions}
                        onStop={handleStop}
                        t={t}
                    />
                ))}

                {/* New session form */}
                <Card
                    className='bg-white rounded-2'
                    title={
                        <Row justify='space-between' align='middle' style={{ width: '100%' }}>
                            <Col>
                                <Button
                                    type='primary'
                                    icon={<PlayCircleOutlined />}
                                    loading={loading}
                                    onClick={handleStart}
                                >
                                    {t('support_start')}
                                </Button>
                            </Col>
                            <Col>
                                <Text strong>{t('support_neue_sitzung')}</Text>
                            </Col>
                        </Row>
                    }
                >
                    <Form form={form} layout='vertical'>
                        <Row gutter={16}>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item name='kundenId' label={t('support_kundenId')}>
                                    <AutoComplete
                                        placeholder='z.B. K-10042'
                                        options={kundenOptions.map(k => ({ value: k.kundenId, label: `${k.kundenId} – ${k.kundenName}` }))}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        onSelect={(val) => {
                                            const kunde = kundenOptions.find(k => k.kundenId === val);
                                            if (kunde) form.setFieldValue('kundenName', kunde.kundenName);
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item name='kundenName' label={t('support_kundenName')}>
                                    <Input placeholder='z.B. Mustermann GmbH' />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item name='ansprechpartner' label={t('support_ansprechpartner')}>
                                    <Input placeholder='z.B. Max Mustermann' />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider>{t('support_taetigkeiten')}</Divider>

                        {taetigkeiten.map((row, idx) => (
                            <Row key={row.key} gutter={8} align='middle' style={{ marginBottom: 8 }}>
                                <Col flex='none'>
                                    <Space>
                                        <Button icon={<PlusOutlined />} onClick={addRow} />
                                        {taetigkeiten.length > 1 && (
                                            <Button icon={<DeleteOutlined />} danger onClick={() => removeRow(row.key)} />
                                        )}
                                    </Space>
                                </Col>
                                <Col flex='1'>
                                    <Select
                                        allowClear
                                        showSearch
                                        placeholder={t('support_taetigkeit_waehlen')}
                                        style={{ width: '100%' }}
                                        options={activityOptions}
                                        value={row.taetigkeitId || undefined}
                                        onChange={v => updateRow(row.key, 'taetigkeitId', v)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                </Col>
                                <Col flex='1'>
                                    <Input
                                        placeholder={t('support_freitext')}
                                        value={row.freitext}
                                        onChange={e => updateRow(row.key, 'freitext', e.target.value)}
                                    />
                                </Col>
                            </Row>
                        ))}

                        <Divider />

                        <Checkbox
                            checked={berechenbar}
                            onChange={e => setBerechenbar(e.target.checked)}
                        >
                            {t('support_berechenbar')}
                        </Checkbox>
                    </Form>
                </Card>
            </Flex>
        </Fragment>
    );
};

export default SupporterfassungPage;
